import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import { IconButton } from '@material-ui/core';
import { foldedLeftNavWidth, leftNavWidth } from '@marqvision/mds/core/LeftNavBar';
import {
  IcoAccountFill,
  IcoArrowDownOutline,
  IcoCheckOutline,
  IcoErrorWarningFill,
  IcoSettingsFill,
} from '@marqvision/mds/assets';
import { MDSChip, MDSDropDown, MDSTheme, MDSTooltip, MDSTypography } from '@marqvision/mds/core';
import { companyProfileAtom } from '~/atoms';
import { useAuth, useLogoutMutation, useSelectBrandId } from '~/apis/auth';
import LoadingCircularProgress from '~/components/LoadingCircularProgress';
import { useIpBrandListQuery } from '~/apis/portfolio/ipDocuments';
import useLogger from '~/hooks/useLogger';
import Dropdown from '../../Popover/Dropdown';

export const headerHeight = '56px';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    appBar: {
      backgroundColor: MDSTheme.palette.bluegray[850],
      boxShadow: 'none',
      height: headerHeight,
      width: `calc(100% - ${foldedLeftNavWidth}px)`,
      marginLeft: foldedLeftNavWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
    appBarShift: {
      width: `calc(100% - ${leftNavWidth}px)`,
      marginLeft: leftNavWidth,
    },
    toolbar: {
      height: headerHeight,
      minHeight: headerHeight,
      display: 'flex',
      justifyContent: 'space-between',
      paddingLeft: '32px',
    },
    toolbarText: {
      display: 'flex',
      alignItems: 'center',
    },
    menuButton: {
      marginRight: theme.spacing(2),
    },
    hide: {
      display: 'none',
    },
    toolbarButtons: {
      display: 'flex',
      gap: '16px',
    },
    actionButtons: {
      marginLeft: '16px',
      alignItems: 'center',
      display: 'flex',
      gap: '8px',
    },
    iconButton: {
      '& svg': {
        width: '16px',
        marginRight: '4px',
        '& path': {
          fill: 'white',
        },
      },
    },
  })
);

interface IProps {
  openNav: boolean;
  title: ReactNode;
  brandUnselectable?: boolean;
  isBrandChipDisabled?: boolean;
  actionButton?: ReactNode;
  menuButton?: ReactNode;
}

const Header = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const navigate = useNavigate();

  const { amplitudeLogging } = useLogger();

  const { openNav, title, isBrandChipDisabled, actionButton, menuButton } = props;
  const [anchorEl, setAnchorEl] = useState(null);

  const companyProfile = useAtomValue(companyProfileAtom);

  const { data: auth } = useAuth();

  const brandId = auth?.selectedBrandId || null;
  const { data: brandList } = useIpBrandListQuery(!!auth);
  const { mutate, isLoading } = useLogoutMutation();
  const { mutate: selectBrand } = useSelectBrandId();

  const brandUnselectable = !!props.brandUnselectable && !!brandId;

  const brandOption = [
    { label: 'All brands', value: null },
    ...(brandList?.brand_list.map((brand) => ({ label: brand.name, value: brand.brand_id })) || []),
  ];

  const handleUserPopoverOpen = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserPopoverClose = () => {
    setAnchorEl(null);
  };

  const handleLink = (link: string) => {
    navigate({ pathname: link });
  };

  const handleLogout = () => {
    amplitudeLogging('sign out');
    mutate({
      access_token: auth?.access_token,
    });
  };

  const handleChangeBrand = (value: number) => {
    selectBrand({
      brandId: value,
    });
  };

  useEffect(() => {
    if (brandList) {
      if (brandId && !brandList.brand_list.some((v) => v.brand_id === brandId)) {
        selectBrand({
          brandId: undefined,
        });
      }
      if (brandList.brand_list.length === 1) {
        selectBrand({
          brandId: brandList.brand_list[0].brand_id,
        });
      }
    }
  }, [selectBrand, brandList, brandId]);

  return (
    <AppBar
      position="fixed"
      className={clsx(classes.appBar, {
        [classes.appBarShift]: openNav,
      })}
    >
      <Toolbar className={classes.toolbar}>
        <div className={classes.toolbarText}>
          <MDSTypography variant="T20" weight="medium" style={{ color: MDSTheme.palette.white }}>
            {title}
          </MDSTypography>
          <div className={classes.actionButtons}>{actionButton}</div>
        </div>
        <div className={classes.toolbarButtons}>
          {brandList &&
            brandList.brand_list.length > 1 &&
            (isBrandChipDisabled ? (
              <MDSChip
                variant="outline"
                size="medium"
                color={brandId ? 'blue' : 'white'}
                icon={brandId ? <IcoCheckOutline /> : undefined}
                endIcon={<IcoArrowDownOutline />}
                disabled={true}
                label={
                  (brandId && brandList.brand_list.find(({ brand_id }) => brand_id === brandId)?.name) || 'All brands'
                }
              />
            ) : brandUnselectable ? (
              <MDSTooltip title="This page can't be viewed by brand">
                <div>
                  <MDSChip variant="tint" color="red" size="medium" icon={<IcoErrorWarningFill />} label="All brands" />
                </div>
              </MDSTooltip>
            ) : (
              <MDSDropDown
                renderAnchorComponent={(items, open) => (
                  <MDSChip
                    variant="outline"
                    size="medium"
                    color={brandId ? 'blue' : 'white'}
                    icon={brandId ? <IcoCheckOutline /> : undefined}
                    endIcon={<IcoArrowDownOutline />}
                    label={brandOption.find((row) => row.value === brandId)?.label}
                    onClick={open}
                  />
                )}
                value={brandId}
                list={brandOption}
                onChange={(value) => handleChangeBrand(value as number)}
              />
            ))}

          {menuButton}

          <IconButton className={classes.iconButton} onClick={() => handleLink('/settings')}>
            <IcoSettingsFill />
            <MDSTypography variant="T14" weight="medium" style={{ color: MDSTheme.palette.white }}>
              Settings
            </MDSTypography>
          </IconButton>
          <IconButton className={classes.iconButton} onClick={handleUserPopoverOpen}>
            <IcoAccountFill />
            <MDSTypography variant="T14" weight="medium" style={{ color: MDSTheme.palette.white }}>
              {companyProfile?.first_name}
            </MDSTypography>
          </IconButton>
        </div>
        <LoadingCircularProgress open={isLoading} />
        <Dropdown
          anchorEl={anchorEl}
          onClose={handleUserPopoverClose}
          items={[{ name: 'Sign out', onClick: handleLogout }]}
        />
      </Toolbar>
    </AppBar>
  );
};

export default Header;
